/**
 * Calculates totals and averages for all stages and their tasks, including all subtasks and direct materials.
 *
 * @param {Array} stages - Array of stage objects, each with tasks and/or sub-stages.
 * @returns {Object} stageSummaries - Totals and averages for each stage, including cost, claimed amount, percentages, and user input history.
 *
 * Use case: Used to calculate and summarize all cost, claim, and percentage data for each stage, including all subtasks and direct materials, for invoice and progress reporting.
 */

import _ from 'lodash';
import { toDecimal, roundValue, Decimal } from '../../../util/decimal-utils';

/**
 * Calculates totals and averages for each stage, rolling up from tasks and sub-stages.
 * Handles user input for claim amounts and percentages, and computes all summary fields.
 * Uses Decimal.js for exact precision calculations.
 */
export const calculateStageAveragesAndTotal = (stages, claimData = {}) => {
  const { progressClaims = [], progressClaimId, selectedTask = [] } = claimData;
  const stageSummaries = {};

  // Helper function to process tasks within a single stage
  const processTasks = (tasks, stageId) => {
    if (!stageSummaries[stageId]) {
      stageSummaries[stageId] = {
        totalCost: new Decimal(0),
        taskClaimedAmount: new Decimal(0),
        totalPercentageComplete: new Decimal(0),
        taskCount: 0,
        overallClaimPercentage: new Decimal(0),
        stillToClaimPercentage: new Decimal(0),
        // Store original user input for claim amount and percentage
        userClaimAmounts: [],
        userClaimPercentages: [],
      };
    }

    tasks.forEach((task, idx) => {
      // Calculate total cost for this task using Decimal
      const taskTotalCost = toDecimal(task.allocatedMaterialCost || task.materialTotal_SaleAmount || 0)
        .plus(toDecimal(task.allocatedLaborCost || task.laborTotal_SaleAmount || 0))
        .plus(toDecimal(task.allocatedSubContractorCost || task.subcontractorTotal_SaleAmount || 0));

      // Calculate exact claimed amount from progress claims history + current claim
      // This ensures we use exact saved amounts instead of recalculating from percentages

      // Get previous claims total from history (exact saved amounts) using Decimal
      let previousClaimedAmount = new Decimal(0);
      if (Array.isArray(progressClaims)) {
        progressClaims.forEach(claim => {
          if (Array.isArray(claim.progressClaimTask) && claim.u_id !== progressClaimId) {
            const taskClaim = claim.progressClaimTask.find(t => t.taskId === task.id);
            if (taskClaim) {
              previousClaimedAmount = previousClaimedAmount.plus(toDecimal(taskClaim.total || 0));
            }
          }
        });
      }

      // Get current claim amount from selectedTask (exact user input) using Decimal
      const selectedTaskData = selectedTask.find(st => st.id === task.id);
      const currentClaimAmount = selectedTaskData ? toDecimal(selectedTaskData.remainingAmount || 0) : new Decimal(0);
      const currentClaimPercentage = selectedTaskData ? toDecimal(selectedTaskData.remainingPercentage || 0) : new Decimal(0);

      // Total claimed amount = previous claims + current claim (all exact amounts) using Decimal
      const taskClaimedAmount = previousClaimedAmount.plus(currentClaimAmount);

      // Calculate percentage for display (from exact amounts) using Decimal
      const claimPercentage = taskTotalCost.greaterThan(0)
        ? taskClaimedAmount.mul(100).div(taskTotalCost)
        : new Decimal(0);

      // Aggregate all values for this stage using Decimal
      stageSummaries[stageId].totalCost = stageSummaries[stageId].totalCost.plus(taskTotalCost);
      stageSummaries[stageId].taskClaimedAmount = stageSummaries[stageId].taskClaimedAmount.plus(taskClaimedAmount);
      stageSummaries[stageId].totalPercentageComplete = stageSummaries[stageId].totalPercentageComplete.plus(claimPercentage);
      stageSummaries[stageId].taskCount += 1;
      stageSummaries[stageId].userClaimAmounts.push(
        currentClaimAmount.toNumber()
      );
      stageSummaries[stageId].userClaimPercentages.push(
        currentClaimPercentage.toNumber()
      );

      // Calculate overall claim percentage using Decimal
      stageSummaries[stageId].overallClaimPercentage = taskTotalCost.greaterThan(0)
        ? taskClaimedAmount.mul(100).div(taskTotalCost)
        : new Decimal(0);

      // Calculate still to claim percentage using Decimal
      stageSummaries[stageId].stillToClaimPercentage = taskTotalCost.greaterThan(0)
        ? taskTotalCost.minus(taskClaimedAmount).mul(100).div(taskTotalCost)
        : new Decimal(0);
    });
  };

  /**
   * Recursively traverses all stages and sub-stages, rolling up totals to parents.
   */
  const traverseStages = (stages, parentId = null) => {
    stages &&
      Array.isArray(stages) &&
      stages.forEach((stage) => {
        const currentStageId = stage.id;

        // Process tasks in the current stage
        if (Array.isArray(stage.tasks) && stage.tasks) {
          processTasks(stage.tasks, currentStageId);
        }

        // Process sub-stages recursively
        if (Array.isArray(stage.stages) && stage.stages) {
          traverseStages(stage.stages, currentStageId);
        }

        // NEW: Special handling for Attribution Level sections
        // If a section is marked for attribution-level claiming, the header totals 
        // should reflect the section claim record itself, not just the hidden child tasks.
        const isAttributionLevel = stage.is_attribution_level === true || stage.lock_flag === true;
        if (isAttributionLevel) {
          // Initialize summary if it doesn't exist
          if (!stageSummaries[currentStageId]) {
            stageSummaries[currentStageId] = {
              totalCost: new Decimal(0),
              taskClaimedAmount: new Decimal(0),
              totalPercentageComplete: new Decimal(0),
              taskCount: 0,
              userClaimAmounts: [],
              userClaimPercentages: [],
            };
          }

          // 1. Get total cost for the section (should already be rolled up from children)
          const totalCost = stageSummaries[currentStageId].totalCost;

          // 2. Find the section-level claim for this stage in the current draft
          const selectedTaskData = selectedTask.find(st => (st.id === currentStageId || st.stageId === currentStageId) && st.type === 'SECTION');
          const currentClaimAmount = selectedTaskData ? toDecimal(selectedTaskData.remainingAmount || 0) : new Decimal(0);
          const currentClaimPercentage = selectedTaskData ? toDecimal(selectedTaskData.remainingPercentage || 0) : new Decimal(0);

          // 3. Get historical section-level claims (exact saved amounts)
          // Aggregates both specific section claims and historical child task claims
          let previousSectionClaimedAmount = new Decimal(0);

          // Function to collect all task IDs in this section hierarchy
          const getAllChildTaskIds = (s) => {
            let ids = [];
            if (s.tasks && Array.isArray(s.tasks)) ids.push(...s.tasks.map(t => t.id));
            if (s.stages && Array.isArray(s.stages)) {
              s.stages.forEach(nested => ids.push(...getAllChildTaskIds(nested)));
            }
            return ids;
          };
          const hierarchyTaskIds = getAllChildTaskIds(stage);

          if (Array.isArray(progressClaims)) {
            progressClaims.forEach(claim => {
              if (Array.isArray(claim.progressClaimTask) && claim.u_id !== progressClaimId) {
                // Find all tasks/sections belonging to this hierarchy in this historical claim
                const matchingTasks = claim.progressClaimTask.filter(t =>
                  t.stageId === currentStageId ||
                  t.stageId === stage.id ||
                  (t.taskId && hierarchyTaskIds.includes(t.taskId))
                );

                if (matchingTasks.length > 0) {
                  // Prefer section-level claims if they exist in this history record to avoid double counting
                  const sectionClaims = matchingTasks.filter(t => t.type === 'SECTION');
                  const tasksToAggregate = sectionClaims.length > 0 ? sectionClaims : matchingTasks;

                  tasksToAggregate.forEach(t => {
                    previousSectionClaimedAmount = previousSectionClaimedAmount.plus(toDecimal(t.total || 0));
                  });
                }
              }
            });
          }

          // 4. OVERRIDE: For attribution sections, the section claim IS the claimed amount for the header
          const totalSectionClaimedAmount = previousSectionClaimedAmount.plus(currentClaimAmount);

          stageSummaries[currentStageId].taskClaimedAmount = totalSectionClaimedAmount;
          stageSummaries[currentStageId].userClaimAmounts = [currentClaimAmount.toNumber()];
          stageSummaries[currentStageId].userClaimPercentages = [currentClaimPercentage.toNumber()];
          stageSummaries[currentStageId].taskCount = 1; // Treat the whole section as 1 item for percentage calculations
        }

        // Roll up to parent if parentId exists
        if (parentId) {
          if (!stageSummaries[parentId]) {
            stageSummaries[parentId] = {
              totalCost: new Decimal(0),
              taskClaimedAmount: new Decimal(0),
              totalPercentageComplete: new Decimal(0),
              taskCount: 0,
              userClaimAmounts: [],
              userClaimPercentages: [],
            };
          }
          // Add current stage's totals to parent using Decimal
          stageSummaries[parentId].totalCost = stageSummaries[parentId].totalCost.plus(
            stageSummaries[currentStageId]?.totalCost || new Decimal(0)
          );
          stageSummaries[parentId].taskClaimedAmount = stageSummaries[parentId].taskClaimedAmount.plus(
            stageSummaries[currentStageId]?.taskClaimedAmount || new Decimal(0)
          );
          stageSummaries[parentId].totalPercentageComplete = stageSummaries[parentId].totalPercentageComplete.plus(
            stageSummaries[currentStageId]?.totalPercentageComplete || new Decimal(0)
          );
          stageSummaries[parentId].taskCount +=
            stageSummaries[currentStageId]?.taskCount || 0;
          stageSummaries[parentId].userClaimAmounts = stageSummaries[
            parentId
          ].userClaimAmounts.concat(
            stageSummaries[currentStageId]?.userClaimAmounts || []
          );
          stageSummaries[parentId].userClaimPercentages = stageSummaries[
            parentId
          ].userClaimPercentages.concat(
            stageSummaries[currentStageId]?.userClaimPercentages || []
          );
        }
      });
  };

  // Start traversal from top-level stages
  traverseStages(stages);

  // Calculate averages for each stage and convert to numbers for return
  Object.keys(stageSummaries).forEach((stageId) => {
    const summary = stageSummaries[stageId];
    const totalCost = summary.totalCost;
    const taskClaimedAmount = summary.taskClaimedAmount;

    stageSummaries[stageId] = {
      totalCost: roundValue(summary.totalCost),
      totalClaimedAmount: roundValue(summary.taskClaimedAmount),
      totalPercentageComplete: roundValue(summary.totalPercentageComplete),
      overallClaimPercentage: roundValue(
        totalCost.greaterThan(0) ? taskClaimedAmount.mul(100).div(totalCost) : new Decimal(0)
      ),
      stillToClaimPercentage: roundValue(
        totalCost.greaterThan(0) ? totalCost.minus(taskClaimedAmount).mul(100).div(totalCost) : new Decimal(0)
      ),
      userClaimAmounts: summary.userClaimAmounts,
      userClaimPercentages: summary.userClaimPercentages,
    };
  });
  return stageSummaries;
};

/**
 * Returns the sum of completion percentages for all approved/finalised claims for the given task IDs.
 * Uses Decimal.js for exact precision calculations.
 * 
 * @param {Object} params
 * @param {Array} params.data - Array of progress claim objects.
 * @param {Array} params.taskIds - Array of task IDs to filter.
 * @returns {number} Sum of completion percentages for the given tasks.
 * 
 * @description
 * Calculates the total completion percentage for specified tasks across all approved claims.
 * Uses Decimal.js to ensure exact precision in percentage summation.
 * 
 * @example
 * const totalPercentage = getCompletionPercentages({
 *   data: progressClaims,
 *   taskIds: ['task-1', 'task-2']
 * });
 * // Returns: 75.50 (exact sum of percentages)
 */
export const getCompletionPercentages = ({ data = [], taskIds = [] }) => {
  if (!data || !Array.isArray(data) || !taskIds || !Array.isArray(taskIds))
    return 0;

  const approvedClaims = data.filter(
    (item) =>
      item.progressClaimStatus === 'Approved' ||
      item.progressClaimStatus === 'Finalised'
  );

  if (!approvedClaims.length) return 0;

  let totalPercentage = new Decimal(0);

  approvedClaims.forEach((claim) => {
    const tasks = claim.progressClaimTask;
    if (tasks && Array.isArray(tasks)) {
      tasks.forEach((task) => {
        if (taskIds.includes(task.taskId) && task.percentageComplete !== undefined) {
          totalPercentage = totalPercentage.plus(toDecimal(task.percentageComplete));
        }
      });
    }
  });

  return roundValue(totalPercentage);
};
